import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback } from "react";
import type { TanStackRouteSearch } from "@/types/url-params.types";

const SAVED_VIEW_CLEARING_KEYS: ReadonlyArray<keyof TanStackRouteSearch> = [
  "selected",
  "filterDays",
  "startDate",
  "endDate",
  "keyMessageConjunction",
];

/**
 * Merges search updates and drops `savedViewId` when filter or date params change.
 */
export const mergeSearchWithSavedViewClear = (
  prev: Partial<TanStackRouteSearch>,
  updates: Partial<TanStackRouteSearch>,
): Partial<TanStackRouteSearch> => {
  const shouldClearSavedViewId = SAVED_VIEW_CLEARING_KEYS.some(
    (key) => key in updates,
  );
  const merged = { ...prev, ...updates };
  if (shouldClearSavedViewId) {
    return { ...merged, savedViewId: undefined };
  }
  return merged;
};

/**
 * Merges partial search into the current location and replaces history
 * (same pathname).
 */
const useMergeSearchNavigate = () => {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return useCallback(
    (updates: Partial<TanStackRouteSearch>) => {
      navigate({
        to: pathname,
        search: (prev) => mergeSearchWithSavedViewClear(prev, updates),
        replace: true,
      });
    },
    [navigate, pathname],
  );
};

export default useMergeSearchNavigate;
